import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

export default function PaymentReturn() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "polling", payment_status: null, amount_total: 0, currency: "gbp", package_name: "" });
  const attempts = useRef(0);
  const navigate = useNavigate();

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState({ ...data, status: "paid" });
          return;
        }
        if (data.status === "expired") {
          setState({ ...data, status: "expired" });
          return;
        }
        attempts.current += 1;
        if (attempts.current >= 10) {
          setState({ ...data, status: "timeout" });
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        attempts.current += 1;
        if (attempts.current >= 10) { setState({ ...state, status: "error" }); return; }
        setTimeout(poll, 2000);
      }
    };
    poll();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [sessionId]);

  return (
    <section className="min-h-[calc(100vh-5rem)] flex items-center justify-center bg-pearl py-20">
      <div className="max-w-md w-full bg-white border border-navy/10 p-10 rounded-sm text-center" data-testid="payment-return">
        {state.status === "polling" && (
          <>
            <Loader2 className="mx-auto text-gold animate-spin" size={44} />
            <h1 className="mt-6 font-display text-3xl text-navy">Confirming your payment…</h1>
            <p className="mt-2 text-slate-600 text-sm">Please don't close this window.</p>
          </>
        )}
        {state.status === "paid" && (
          <>
            <CheckCircle2 className="mx-auto text-saffron" size={56} />
            <h1 className="mt-6 font-display text-3xl text-navy">Thank you.</h1>
            <p className="mt-3 text-slate-700">
              Your payment for <strong>{state.package_name}</strong> ({state.currency?.toUpperCase()} {(state.amount_total / 100).toFixed(2)}) has been received.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              <button onClick={() => navigate("/dashboard")} data-testid="pr-dashboard-btn" className="px-6 py-3 bg-navy text-pearl rounded-sm">Go to dashboard</button>
              <Link to="/" className="px-6 py-3 border border-navy/20 text-navy rounded-sm">Return home</Link>
            </div>
          </>
        )}
        {(state.status === "expired" || state.status === "error" || state.status === "timeout") && (
          <>
            <XCircle className="mx-auto text-saffron" size={56} />
            <h1 className="mt-6 font-display text-3xl text-navy">Payment not completed.</h1>
            <p className="mt-3 text-slate-600 text-sm">{state.status === "expired" ? "Session expired." : "We couldn't confirm your payment."} Please try again.</p>
            <div className="mt-8 flex gap-3 justify-center">
              <Link to="/donate" className="px-6 py-3 bg-navy text-pearl rounded-sm">Try donating again</Link>
              <Link to="/" className="px-6 py-3 border border-navy/20 text-navy rounded-sm">Return home</Link>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
